import {
    useCallback,
    useEffect,
    useState,
} from 'react'

const PRELOAD_MARGIN = '200px'

type Params = {
    onLoadMore: () => void
    isEnabled: boolean
    scrollContainer?: HTMLElement | null
}

// Returns a ref for an empty "sentinel" element placed after the last list item.
// An IntersectionObserver calls onLoadMore when the sentinel comes within
// PRELOAD_MARGIN of the visible part of scrollContainer (viewport if omitted).
const useLoadMoreOnScroll = ({
    onLoadMore,
    isEnabled,
    scrollContainer = null,
}: Params) => {
    const [
        sentinel,
        setSentinel,
    ] = useState<HTMLElement | null>(null)

    const sentinelRef = useCallback((element: HTMLElement | null) => {
        setSentinel(element)
    }, [])

    useEffect(() => {
        if (!sentinel || !isEnabled) {
            return undefined
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                onLoadMore()
            }
        }, {
            root: scrollContainer,
            rootMargin: PRELOAD_MARGIN,
        })

        observer.observe(sentinel)

        return () => {
            observer.disconnect()
        }
    }, [
        sentinel,
        isEnabled,
        onLoadMore,
        scrollContainer,
    ])

    return sentinelRef
}

export default useLoadMoreOnScroll
